/**
 * Prepares the final object for email sending
 * @param {Object} emailData - Basic email data with required fields
 * @param {Object} bookingData - Complete booking data from form (optional)
 * @param {Object} pricingData - Flight pricing data with flight details (optional)
 * @param {Object} apiBookingResponse - Response from the booking API (optional)
 * @param {Object} extraBaggage - Additional baggage selected by the user (optional)
 * @returns {Object} - Complete object with data prepared for email sending
 */
export const prepareEmailData = (
  emailData,
  bookingData = null,
  pricingData = null,
  apiBookingResponse = null,
  extraBaggage = null
) => {
  if (!emailData.emailAgency || !emailData.email) {
    throw new Error('Fields emailAgency and email are required');
  }

  const bookingLine = apiBookingResponse?.booking?.lines?.[0];
  const bookingRef = bookingLine?.booking_reference;
  const holder = bookingLine?.holder;
  const apiTravellers = bookingLine?.travellers;
  const travelInfo = bookingLine?.travel;

  // Primero preparamos los supplements
  const supplements = prepareSupplementsData(bookingData, apiTravellers, travelInfo);

  // Luego combinamos con el equipaje extra si existe
  const finalSupplements = extraBaggage
    ? combineSupplements(supplements, extraBaggage, travelInfo)
    : supplements;

  // Ahora preparamos el objeto final con los supplements ya procesados
  const processedData = {
    email_agency: emailData.emailAgency,
    name_agency: emailData.nameAgency || '',
    agency_address: emailData.agencyAddress || '',
    agency_phone: emailData.agencyPhone || '',
    name: holder?.name || emailData.name || '',
    surnames: holder?.surnames?.[0] || emailData.surnames || '',
    id_booking: apiBookingResponse?.booking?.id || '',
    locator: bookingRef?.locator || emailData.locator || '',
    third_locator: bookingRef?.third_locator || '',
    last_ticket_date: travelInfo?.last_ticket_date || '',
    email: holder?.contact?.mails?.[0] || emailData.email,
    phone: holder?.contact?.phones?.[0]?.number || emailData.phone || '',
    external_view: bookingRef?.external_view || '',
    travellers: prepareTravellersData(bookingData, apiTravellers),
    travel: [{ flights: prepareFlightData(pricingData, travelInfo) }],
    price_breakdown: preparePriceBreakdown(
      {
        ...pricingData,
        supplements: finalSupplements, // Usamos los supplements ya procesados
      },
      travelInfo
    ),
    supplements: finalSupplements,
  };

  // Update contact info if available in booking data
  if (bookingData?.contactInfo?.data) {
    updateContactInfo(processedData, bookingData.contactInfo.data);
  }

  return processedData;
};

/**
 * Combines existing supplements with additional baggage
 * @param {Array} existingSupplements - Existing supplements data
 * @param {Object} extraBaggage - Additional baggage selected by the user
 * @param {Object} travelInfo - Travel info from API response
 * @returns {Array} - Combined supplements data
 */
const combineSupplements = (existingSupplements, extraBaggage, travelInfo) => {
  const combinedSupplements = [...existingSupplements];
  let totalExtraBaggagePrice = 0;

  // Calculate total price from extra baggage
  Object.values(extraBaggage).forEach((baggageItems) => {
    baggageItems.forEach((item) => {
      totalExtraBaggagePrice += item.total || 0;
    });
  });

  // Create a mapping of segment references to their numeric indices
  const segmentMapping = {};
  if (travelInfo?.services_groups) {
    let segmentIndex = 1;
    travelInfo.services_groups.forEach((group) => {
      group.services.forEach((service) => {
        service.segments.forEach((segment, index) => {
          const segmentRef = `S${index + 1}`;
          segmentMapping[segmentRef] = segmentIndex.toString();
          segmentIndex++;
        });
      });
    });
  }

  // Iterate over the extra baggage object
  Object.entries(extraBaggage).forEach(([passengerKey, baggageItems]) => {
    const passengerIndex = parseInt(passengerKey.split('_')[1]);
    const passenger = combinedSupplements[0]?.passengers[passengerIndex];

    if (passenger) {
      const baggageMap = new Map();

      // First, add existing baggage to the map
      passenger.baggage.forEach((bag) => {
        const segRef = bag.seg_references[0];
        baggageMap.set(segRef, {
          ...bag,
          extra: [...bag.extra],
        });
      });

      // Then, process extra baggage items
      baggageItems.forEach((item) => {
        const numericSegRef = segmentMapping[item.seg_references[0]] || '1';

        if (!baggageMap.has(numericSegRef)) {
          baggageMap.set(numericSegRef, {
            included: [],
            extra: [],
            seg_references: [numericSegRef],
            journey_references: item.journey_references.map((ref) => ({
              iata: ref,
              city_name: '',
              country_name: '',
            })),
          });
        }

        baggageMap.get(numericSegRef).extra.push({
          short_name: item.short_name,
          long_name: item.short_name,
          price: item.total,
        });
      });

      passenger.baggage = Array.from(baggageMap.values());
    }
  });

  // Add the total extra baggage price to the first supplement
  if (combinedSupplements[0]) {
    combinedSupplements[0].totalExtraBaggagePrice = totalExtraBaggagePrice;
  }

  return combinedSupplements;
};

/**
 * Determines passenger type from personal info
 * @param {Object} personalInfo - Passenger personal info
 * @returns {string} - Passenger type (ADT, CHD, INF)
 */
const determinePassengerType = (personalInfo) => {
  const type = personalInfo.passengerType?.toLowerCase() || personalInfo.type?.toLowerCase();
  if (type === 'niño' || type === 'nino' || type === 'chd' || type === 'child') {
    return 'CHD';
  }
  if (type === 'bebé' || type === 'bebe' || type === 'inf' || type === 'infant') {
    return 'INF';
  }
  return 'ADT';
};

/**
 * Prepares travellers data
 * @param {Object} bookingData - Booking data
 * @param {Array} apiTravellers - Travellers from API response
 * @returns {Array} - Formatted travellers data
 */
const prepareTravellersData = (bookingData, apiTravellers = null) => {
  if (apiTravellers && apiTravellers.length > 0) {
    return apiTravellers.map((traveller, index) => ({
      index,
      source_id: `T${index + 1}`,
      type: traveller.type || '',
      title: traveller.title || determineTitle({ gender: traveller.gender }),
      name: traveller.name || '',
      surnames: traveller.surnames || [],
      document: traveller.documents?.[0]
        ? {
            type: traveller.documents[0].type || '',
            number: traveller.documents[0].number || '',
          }
        : { type: '', number: '' },
      fiscalData: traveller.fiscalData || {
        documentType: '',
        documentNumber: '',
        cuitCuil: '',
      },
    }));
  }

  return (
    bookingData?.passengers
      ?.filter((passenger) => passenger?.personalInfo)
      .map((passenger, index) => {
        const fiscalData = bookingData.fiscalData?.data?.[index] || {
          documentType: '',
          documentNumber: '',
          cuitCuil: '',
        };

        return {
          source_id: `T${index + 1}`,
          index,
          type: determinePassengerType(passenger.personalInfo),
          title: determineTitle(passenger.personalInfo),
          name: passenger.personalInfo.name || '',
          surnames: [passenger.personalInfo.lastName || ''],
          document: {
            type: passenger.personalInfo.documentType || '',
            number: passenger.personalInfo.documentNumber || '',
          },
          fiscalData,
        };
      }) || []
  );
};

/**
 * Determines title from gender
 * @param {Object} personalInfo - Passenger personal info
 * @returns {string} - Title (Mr or Mrs)
 */
const determineTitle = (personalInfo) => {
  if (personalInfo.gender === 'Female') {
    return 'Mrs';
  }
  return 'Mr';
};

/**
 * Prepares flight data for the email
 * @param {Object} pricingData - Flight pricing data
 * @param {Object} travelInfo - Travel info from API response
 * @returns {Array} - Formatted flight data
 */
const prepareFlightData = (pricingData, travelInfo = null) => {
  if (pricingData?.pricing?.journeys) {
    return formatJourneys(pricingData.pricing.journeys);
  }

  if (travelInfo?.services_groups) {
    return travelInfo.services_groups.flatMap((group) =>
      group.services.map((service) => ({
        date: new Date(service.segments[0].departure_date_time).toLocaleDateString('en-US', {
          weekday: 'short',
          day: '2-digit',
          month: 'short',
        }),
        duration: formatDuration(service.duration),
        segments: service.segments.map((segment, idx) =>
          formatSegment(segment, idx, service.segments)
        ),
      }))
    );
  }

  return [];
};

/**
 * Formats journey data into flight objects
 * @param {Object} journeys - Journey data
 * @returns {Array} - Formatted flight data
 */
const formatJourneys = (journeys) => {
  const flights = [];
  if (journeys.outbound) {
    flights.push(createFlightObject(journeys.outbound));
  }
  if (journeys.return) {
    flights.push(createFlightObject(journeys.return));
  }
  return flights;
};

/**
 * Creates a flight object from journey data
 * @param {Object} journey - Journey data
 * @returns {Object} - Formatted flight object
 */
const createFlightObject = (journey) => {
  const segments = journey.segments || [];
  return {
    date: new Date(segments[0].departure_date_time).toLocaleDateString('en-US', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    }),
    duration: formatDuration(journey.duration),
    segments: segments.map((segment, idx) => formatSegment(segment, idx, segments)),
  };
};

/**
 * Formats a flight segment
 * @param {Object} segment - Segment data
 * @param {number} idx - Segment index
 * @param {Array} allSegments - All segments in the journey
 * @returns {Object} - Formatted segment object
 */
const formatSegment = (segment, idx, allSegments) => {
  const departureTime = new Date(segment.departure_date_time).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const arrivalTime = new Date(segment.arrival_date_time).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return {
    departure_time: departureTime,
    departure_airport: formatAirport(
      segment.origin_city_name,
      segment.origin_short_name,
      segment.origin_iata
    ),
    flight_duration: formatDuration(
      (new Date(segment.arrival_date_time) - new Date(segment.departure_date_time)) / 1000
    ),
    arrival_time: arrivalTime,
    arrival_airport: formatAirport(
      segment.destination_city_name,
      segment.destination_short_name,
      segment.destination_iata
    ),
    class: segment.cabin?.toUpperCase() || '',
    fare_class: segment.marketing_name ? `Class ${segment.marketing_name.charAt(0)}` : '',
    flight_number: segment.company_name
      ? `${segment.company_name.split(' ')[0]}${Math.floor(Math.random() * 1000)}`
      : '',
    ...(idx < allSegments.length - 1
      ? { layover: calculateLayover(segment, allSegments[idx + 1]) }
      : {}),
  };
};

/**
 * Formats airport information
 * @param {string} cityName - City name
 * @param {string} shortName - Airport short name
 * @param {string} iata - IATA code
 * @returns {string} - Formatted airport string
 */
const formatAirport = (cityName, shortName, iata) => {
  return [cityName, shortName, iata].filter(Boolean).join(', ') || '';
};

/**
 * Formats duration in seconds to "Xh Ymin" format
 * @param {number} durationInSeconds - Duration in seconds
 * @returns {string} - Formatted duration
 */
const formatDuration = (durationInSeconds) => {
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  return `${hours}h ${minutes}min`;
};

/**
 * Calculates layover time between segments
 * @param {Object} currentSegment - Current segment
 * @param {Object} nextSegment - Next segment
 * @returns {string} - Formatted layover information
 */
const calculateLayover = (currentSegment, nextSegment) => {
  const layoverDurationMs =
    new Date(nextSegment.departure_date_time) - new Date(currentSegment.arrival_date_time);
  const layoverHours = Math.floor(layoverDurationMs / (1000 * 60 * 60));
  const layoverMinutes = Math.floor((layoverDurationMs % (1000 * 60 * 60)) / (1000 * 60));
  return `Waits ${layoverHours}h ${layoverMinutes}min until the next flight`;
};

/**
 * Prepares price breakdown information
 * @param {Object} pricingData - Flight pricing data
 * @param {Object} travelInfo - Travel info from API response
 * @returns {Array} - Price breakdown data
 */
const preparePriceBreakdown = (pricingData, travelInfo = null) => {
  if (pricingData?.pricing?.price && pricingData.pricing.passengers) {
    const priceInfo = pricingData.pricing.price;
    const { passengers } = pricingData.pricing;



    // Calculate included luggage total
    const includedLuggageTotal = Number(
      Array.from(
        Object.values(pricingData?.pricing?.supplements || {}).filter(
          (supp) =>
            (supp.category === 'Baggage' || supp.category === 'HandBaggage') && supp.included
        )
      )
        .reduce((sum, supp) => sum + (supp.amount || 0), 0)
        .toFixed(2)
    );

    // Get extra baggage price from supplements
    const extraBaggagePrice = Number(
      (pricingData?.supplements?.[0]?.totalExtraBaggagePrice || 0).toFixed(2)
    );
    const totalLuggagePrice = Number((includedLuggageTotal + extraBaggagePrice).toFixed(2));

    return [
      {
        currency: priceInfo.currency || '',
        details: Object.entries(passengers).map(([type, data]) => ({
          category: type.toUpperCase(),
          quantity: data.quantity || 0,
          rate: Number((data.base || 0).toFixed(2)),
          taxes: Number((data.total_taxes || 0).toFixed(2)),
          fee: Number((data.fee || 0).toFixed(2)),
          emission_fee: Number((data.emission_fee || 0).toFixed(2)),
          subtotal: Number((data.amount || 0).toFixed(2)),
        })),
        luggage: {
          quantity: calculateTotalLuggageQuantity(pricingData?.supplements),
          total: totalLuggagePrice,
        },
        grand_total: Number(((priceInfo.total || 0) + totalLuggagePrice).toFixed(2)),
      },
    ];
  }

  if (travelInfo?.price) {
    // Calculate included luggage total from travel info
    const includedLuggageTotal = Number(
      Array.from(
        travelInfo?.supplements?.filter(
          (supp) =>
            (supp.category === 'Baggage' || supp.category === 'HandBaggage') && supp.included
        ) || []
      )
        .reduce((sum, supp) => sum + (supp.amount || 0), 0)
        .toFixed(2)
    );

    // Get extra baggage price from supplements
    const extraBaggagePrice = Number(
      (travelInfo?.supplements?.[0]?.totalExtraBaggagePrice || 0).toFixed(2)
    );
    const totalLuggagePrice = Number((includedLuggageTotal + extraBaggagePrice).toFixed(2));

    return [
      {
        currency: travelInfo.price.currency || '',
        details:
          travelInfo.price.fares?.map((fare) => ({
            category: fare.passenger_type_normalized || fare.passenger_type || 'ADT',
            quantity: fare.quantity || 0,
            rate: Number((fare.base || 0).toFixed(2)),
            taxes: Number((fare.total_taxes || 0).toFixed(2)),
            fee: Number((fare.fee || 0).toFixed(2)),
            emission_fee: Number((fare.emission_fee || 0).toFixed(2)),
            subtotal: Number((fare.amount || 0).toFixed(2)),
          })) || [],
        luggage: {
          quantity: calculateTotalLuggageQuantity(travelInfo?.supplements),
          total: totalLuggagePrice,
        },
        grand_total: Number(((travelInfo.price.total || 0) + totalLuggagePrice).toFixed(2)),
      },
    ];
  }

  return [
    {
      currency: '',
      details: [],
      luggage: {
        quantity: 0,
        total: 0,
      },
      grand_total: 0,
    },
  ];
};

/**
 * Calculates the total quantity of extra luggage items
 * @param {Array} supplements - Array of supplements
 * @returns {number} - Total quantity of extra luggage items
 */
const calculateTotalLuggageQuantity = (supplements) => {
  if (!supplements || !supplements[0]?.passengers) return 0;

  return supplements[0].passengers.reduce((total, passenger) => {
    return (
      total +
      passenger.baggage.reduce((passengerTotal, bag) => {
        return passengerTotal + (bag.extra?.length || 0); // Solo contamos el equipaje extra
      }, 0)
    );
  }, 0);
};

/**
 * Prepares supplements data
 * @param {Object} bookingData - Booking data
 * @param {Array} travellers - Travellers data
 * @param {Object} travelInfo - Travel info from API response
 * @returns {Array} - Formatted supplements data
 */
const prepareSupplementsData = (bookingData, travellers, travelInfo = null) => {
  const baggageSupplements =
    travelInfo?.supplements?.filter(
      (supp) => (supp.category === 'Baggage' || supp.category === 'HandBaggage') && supp.included
    ) || [];

  return [
    {
      passengers:
        travellers?.map((traveller, index) => {
          // Create a map to group baggage by seg_references
          const baggageMap = new Map();

          // Process all baggage supplements for this traveller
          baggageSupplements
            .filter(
              (supp) =>
                !supp.pax_references ||
                supp.pax_references.includes(traveller.source_id || `T${index + 1}`)
            )
            .forEach((supp) => {
              const segRef = supp.seg_references?.[0] || '';
              if (!baggageMap.has(segRef)) {
                baggageMap.set(segRef, {
                  included: [],
                  extra: [],
                  seg_references: [segRef],
                  journey_references: [
                    {
                      iata: supp.journey_references?.[0]?.iata || '',
                      city_name: supp.journey_references?.[0]?.city_name || '',
                      country_name: supp.journey_references?.[0]?.country_name || '',
                    },
                  ],
                });
              }
              baggageMap.get(segRef).included.push({
                short_name: supp.short_name || '',
                long_name: supp.short_name || '',
              });
            });

          return {
            name: traveller.name || '',
            surnames: traveller.surnames || [],
            pax_reference: traveller.source_id || `T${index + 1}`, // Usamos el source_id o generamos uno si no existe
            baggage: Array.from(baggageMap.values()),
          };
        }) || [],
    },
  ];
};

/**
 * Updates contact information from booking data
 * @param {Object} processedData - Processed data object to update
 * @param {Object} contactData - Contact data from booking
 */
const updateContactInfo = (processedData, contactData) => {
  if (contactData.nombre) processedData.name = contactData.nombre;
  if (contactData.apellidos) processedData.surnames = contactData.apellidos;
  if (contactData.email) processedData.email = contactData.email;
  if (contactData.telefono1?.numero) {
    processedData.phone = contactData.telefono1.numero;
  }
};
